import React, { useState, useEffect } from 'react';
import { roomsAPI, productsAPI, apiClient } from './services/api';
import PaymentModal from './PaymentModal';
import SalesAgreement from './SalesAgreement';

interface BookingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  roomName: string;
  checkInDate: string;
  checkOutDate: string;
  adults: number;
  children: number;
  childAges: number[];
  totalPrice: number;
  nights: number;
  roomTypeId?: string;
  source?: 'reservation-page' | 'home-calendar' | 'ourrooms-summary' | 'ourrooms-room' | 'ourrooms';
}

const isValidFullName = (n: string) =>
  n.trim().split(/\s+/).filter(w => w.length >= 2).length >= 2;

const BookingFormModal: React.FC<BookingFormModalProps> = ({
  isOpen,
  onClose,
  roomId,
  roomName,
  checkInDate,
  checkOutDate,
  adults: adultsCount,
  children: childrenCount,
  childAges: childrenAges,
  totalPrice: initialPrice,
  nights,
  roomTypeId,
  source = 'reservation-page'
}) => {
  const [loading, setLoading] = useState(false);
  const [roomPrice, setRoomPrice] = useState(initialPrice);
  const [priceLoading, setPriceLoading] = useState(false);

  // Ek Yatak / Kapasite Uyarısı
  const [showExtraBedWarning, setShowExtraBedWarning] = useState(false);
  const [extraBedAcknowledged, setExtraBedAcknowledged] = useState(false);
  const [capacityExceeded, setCapacityExceeded] = useState(false); // maxOccupancy aşıldı - rezervasyon yapılamaz
  const [roomCapacity, setRoomCapacity] = useState<{ standardCapacity: number; maxOccupancy: number } | null>(null);

  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [createdBookingId, setCreatedBookingId] = useState<string | number>('');

  // Discount Code State
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState<any>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountMessage, setDiscountMessage] = useState({ text: '', type: '' });

  // Sales Agreement State
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  // Misafir Grubu Cinsiyeti
  const [guestGroupType, setGuestGroupType] = useState<'' | 'mixed' | 'male_only'>();

  // Ad Soyad
  const [guestName, setGuestName] = useState('');

  // Kahvaltı için kişi sayıları
  const [breakfastAdults, setBreakfastAdults] = useState(0);
  const [breakfastChildren7_12, setBreakfastChildren7_12] = useState(0);
  const [breakfastChildren0_6, setBreakfastChildren0_6] = useState(0);

  // Akşam yemeği için kişi sayıları
  const [dinnerAdults, setDinnerAdults] = useState(0);
  const [dinnerChildren7_12, setDinnerChildren7_12] = useState(0);
  const [dinnerChildren0_6, setDinnerChildren0_6] = useState(0);

  // Meal Add-ons State
  const [includeBreakfast, setIncludeBreakfast] = useState(false);
  const [includeDinner, setIncludeDinner] = useState(false);
  const [dinnerClosedDays, setDinnerClosedDays] = useState<number[]>([1]);
  // Pazartesi/Pazar uyarı onayları
  const [sundayMondayAgreed, setSundayMondayAgreed] = useState(false);
  const [mondayAgreed, setMondayAgreed] = useState(false);
  const [mealPricing, setMealPricing] = useState({
    breakfastAdult: 0,
    breakfastChild7_12: 0,
    breakfastChild0_6: 0,
    dinnerAdult: 0,
    dinnerChild7_12: 0,
    dinnerChild0_6: 0
  });
  const [mealPricesLoaded, setMealPricesLoaded] = useState(false);
  const [mealPriceError, setMealPriceError] = useState(false);

  // Fatura bilgileri
  const [invoiceType, setInvoiceType] = useState<'individual' | 'corporate'>('individual');
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceTckn, setInvoiceTckn] = useState('');
  const [invoiceTaxNumber, setInvoiceTaxNumber] = useState('');
  const [invoiceTaxOffice, setInvoiceTaxOffice] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceDistrict, setInvoiceDistrict] = useState('');
  const [invoiceCity, setInvoiceCity] = useState('');
  const [invoicePostalCode, setInvoicePostalCode] = useState('');

  // Oda fiyatını availability API'den çek + kapasite kontrolü
  useEffect(() => {
    if (!isOpen || !checkInDate || !checkOutDate) return;
    if (initialPrice > 0) {
      setRoomPrice(initialPrice);
      return;
    }
    const fetchPrice = async () => {
      setPriceLoading(true);
      setExtraBedAcknowledged(false);
      setShowExtraBedWarning(false);
      setCapacityExceeded(false);
      setRoomCapacity(null);
      try {
        // Gerçek kişi sayısıyla sorgu at
        const response = await roomsAPI.getAvailability({
          checkIn: checkInDate,
          checkOut: checkOutDate,
          adults: adultsCount,
          children: childrenCount,
          childrenAges: childrenAges
        });
        const rooms = response.data?.data?.rooms || response.data?.data || [];
        const matchedRoom = rooms.find((r: any) =>
          r.roomTypeId === roomId || r.roomTypeId === roomTypeId || r.id === roomId
        );

        if (matchedRoom && matchedRoom.price) {
          // Oda bu kişi sayısını kabul ediyor - fiyat doğru
          setRoomPrice(matchedRoom.price);
          
          // ReservationPage ile aynı: ek yatak gerekiyor mu?
          const stdCap = matchedRoom.standardCapacity || matchedRoom.maxAdults || matchedRoom.maxOccupancy || 3;
          const maxOcc = matchedRoom.maxOccupancy || stdCap;
          setRoomCapacity({ standardCapacity: stdCap, maxOccupancy: maxOcc });
          
          const totalGuests = adultsCount + childrenCount;
          if (totalGuests > stdCap) {
            setShowExtraBedWarning(true);
          }
        } else {
          // API bu odayı döndürmedi → bu oda seçilen kişi sayısını alamaz
          console.log('🚫 Oda bu kişi sayısı için uygun değil');
          setCapacityExceeded(true);
          setRoomPrice(0);
        }
      } catch (error) {
        console.error('Fiyat çekilemedi:', error);
      } finally {
        setPriceLoading(false);
      }
    };
    fetchPrice();
  }, [isOpen, checkInDate, checkOutDate, adultsCount, childrenCount, roomId, roomTypeId, initialPrice, childrenAges]);

  // Yemek fiyatlarını API'den çek
  useEffect(() => {
    if (!isOpen) return;
    const fetchMealPricing = async () => {
      try {
        const [lodgingBreakfastRes, dinnerRes, dinnerPublicRes] = await Promise.all([
          productsAPI.getPublicSetting('lodging_breakfast'),
          productsAPI.getByType('dinner'),
          productsAPI.getPublicSetting('dinner_public').catch(() => null)
        ]);

        const lodgingBreakfastValue = lodgingBreakfastRes.data?.data?.value;
        const dinnerContent = dinnerRes.data?.data?.content;

        const newPricing = {
          breakfastAdult: lodgingBreakfastValue?.price_rules?.adult || 0,
          breakfastChild7_12: lodgingBreakfastValue?.price_rules?.child_7_12 || 0,
          breakfastChild0_6: lodgingBreakfastValue?.price_rules?.child_0_6 || 0,
          dinnerAdult: dinnerContent?.price_rules?.adult || 0,
          dinnerChild7_12: dinnerContent?.price_rules?.child_7_12 || 0,
          dinnerChild0_6: dinnerContent?.price_rules?.child_0_6 || 0
        };

        if (!newPricing.dinnerAdult || !newPricing.breakfastAdult) {
          throw new Error('Fiyat bilgileri eksik');
        }

        setMealPricing(newPricing);
        setMealPricesLoaded(true);
        setMealPriceError(false);

        const dinnerPublicValue = dinnerPublicRes?.data?.data?.value;
        if (dinnerPublicValue) {
          const normalizedDays: number[] = Array.isArray(dinnerPublicValue.closed_days)
            ? Array.from(new Set<number>(dinnerPublicValue.closed_days
                .map((d: any) => Number(d))
                .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6)))
                .sort((a, b) => a - b)
            : [1];
          setDinnerClosedDays(normalizedDays.length > 0 ? normalizedDays : [1]);
        }
      } catch (error) {
        console.error('❌ Yemek fiyatları yüklenemedi!', error);
        setMealPriceError(true);
        setMealPricesLoaded(true);
      }
    };

    fetchMealPricing();
  }, [isOpen]);

  // Modal açıldığında varsayılan kişi sayılarını ayarla
  useEffect(() => {
    if (!isOpen) return;
    const children0_6 = childrenAges.filter(a => a >= 0 && a <= 6).length;
    const children7_12 = childrenAges.filter(a => a >= 7 && a <= 12).length;
    const teenAdults = childrenAges.filter(a => a >= 13).length;
    const effectiveAdults = adultsCount + teenAdults;

    setBreakfastAdults(effectiveAdults);
    setBreakfastChildren7_12(children7_12);
    setBreakfastChildren0_6(children0_6);
    setDinnerAdults(effectiveAdults);
    setDinnerChildren7_12(children7_12);
    setDinnerChildren0_6(children0_6);

    // Reset states
    setIncludeBreakfast(false);
    setIncludeDinner(false);
    setDiscountApplied(null);
    setDiscountCode('');
    setDiscountMessage({ text: '', type: '' });
    setAgreementAccepted(false);
    setSundayMondayAgreed(false);
    setMondayAgreed(false);
    setGuestName('');
    setInvoiceType('individual');
    setInvoiceName('');
    setInvoiceTckn('');
    setInvoiceTaxNumber('');
    setInvoiceTaxOffice('');
    setInvoiceAddress('');
    setInvoiceDistrict('');
    setInvoiceCity('');
    setInvoicePostalCode('');
  }, [isOpen, adultsCount, childrenAges]);

  const getMondayExclusions = (checkIn: string, checkOut: string) => {
    if (!checkIn || !checkOut) return { breakfasts: 0, dinners: 0 };
    let breakfasts = 0, dinners = 0;
    const end = new Date(checkOut + 'T12:00:00');
    for (let d = new Date(checkIn + 'T12:00:00'); d < end; d.setDate(d.getDate() + 1)) {
      if (dinnerClosedDays.includes(d.getDay())) dinners++;
      const morning = new Date(d);
      morning.setDate(morning.getDate() + 1);
      if (dinnerClosedDays.includes(morning.getDay())) breakfasts++;
    }
    return { breakfasts, dinners };
  };

  const calculateMealCosts = () => {
    const excl = getMondayExclusions(checkInDate, checkOutDate);
    const billableBreakfastNights = Math.max(0, nights - excl.breakfasts);
    const billableDinnerNights = Math.max(0, nights - excl.dinners);

    let breakfastCost = 0;
    let dinnerCost = 0;

    if (includeBreakfast) {
      breakfastCost = billableBreakfastNights * (
        breakfastAdults * mealPricing.breakfastAdult +
        breakfastChildren7_12 * mealPricing.breakfastChild7_12 +
        breakfastChildren0_6 * mealPricing.breakfastChild0_6
      );
    }

    if (includeDinner) {
      dinnerCost = billableDinnerNights * (
        dinnerAdults * mealPricing.dinnerAdult +
        dinnerChildren7_12 * mealPricing.dinnerChild7_12 +
        dinnerChildren0_6 * mealPricing.dinnerChild0_6
      );
    }

    return {
      breakfast: breakfastCost,
      dinner: dinnerCost,
      total: breakfastCost + dinnerCost,
      excludedBreakfasts: excl.breakfasts,
      excludedDinners: excl.dinners,
      billableBreakfastNights,
      billableDinnerNights
    };
  };

  const handleApplyDiscount = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) {
      setDiscountMessage({ text: 'Lütfen bir indirim kodu girin', type: 'warning' });
      return;
    }

    const emailInput = document.querySelector('input[name="guestEmail"]') as HTMLInputElement;
    const phoneInput = document.querySelector('input[name="guestPhone"]') as HTMLInputElement;
    const guestEmail = emailInput?.value?.trim() || '';
    const guestPhone = phoneInput?.value?.trim() || '';
    setDiscountLoading(true);
    setDiscountMessage({ text: '', type: '' });

    try {
      const response = await apiClient.post('/modules/hotel/discount-codes/validate', {
        code: code,
        property_id: '9004c629-e08b-428a-8f36-1de7b22af888',
        total_amount: roomPrice || 0,
        guest_phone: guestPhone,
        guest_email: guestEmail,
        room_type_id: roomTypeId || roomId
      });

      const data = response.data;

      if (data.valid && data.discount_code) {
        setDiscountApplied({
          ...data.discount_code,
          discount_amount: data.discount_amount,
          original_amount: data.original_amount,
          final_amount: data.final_amount
        });
        setDiscountMessage({
          text: `✅ İndirim uygulandı! ${data.discount_amount.toFixed(2)} TL indirim kazandınız.`,
          type: 'success'
        });
      } else {
        setDiscountApplied(null);
        setDiscountMessage({
          text: data.msg || 'İndirim kodu geçersiz',
          type: 'error'
        });
      }
    } catch (error: any) {
      console.error('Discount validation error:', error);
      setDiscountApplied(null);
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || 'İndirim kodu kontrol edilirken hata oluştu';
      setDiscountMessage({ text: errorMsg, type: 'error' });
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountApplied(null);
    setDiscountCode('');
    setDiscountMessage({ text: '', type: '' });
  };

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!guestGroupType) {
      alert('Lütfen misafir grubu cinsiyetini seçin.');
      return;
    }
    if (guestGroupType === 'male_only') {
      alert('Villa Polikne, butik aile işletmesi konseptiyle hizmet vermektedir. Yalnızca erkek misafirlerden oluşan grup veya tekil rezervasyonlar kabul edilmemektedir.');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData(e.target as HTMLFormElement);
      const mealCosts = calculateMealCosts();
      const finalRoomPrice = discountApplied ? discountApplied.final_amount : (roomPrice || 0);
      const totalWithMeals = finalRoomPrice + mealCosts.total;

      const bookingData = {
        source,
        roomTypeId: roomTypeId || roomId,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        adults: adultsCount,
        children: childrenCount,
        childrenAges: childrenAges,
        guestName: guestName,
        guestEmail: formData.get('guestEmail') as string,
        guestPhone: formData.get('guestPhone') as string,
        guestGroupType: guestGroupType || 'mixed',
        specialRequests: formData.get('specialRequests') as string,
        totalPrice: totalWithMeals,
        includeBreakfast: includeBreakfast,
        includeDinner: includeDinner,
        breakfastCost: mealCosts.breakfast,
        dinnerCost: mealCosts.dinner,
        mealDetails: {
          breakfast: includeBreakfast ? {
            adults: breakfastAdults,
            children7_12: breakfastChildren7_12,
            children0_6: breakfastChildren0_6,
            totalPeople: breakfastAdults + breakfastChildren7_12 + breakfastChildren0_6,
            nights: mealCosts.billableBreakfastNights,
            totalCost: mealCosts.breakfast
          } : null,
          dinner: includeDinner ? {
            adults: dinnerAdults,
            children7_12: dinnerChildren7_12,
            children0_6: dinnerChildren0_6,
            totalPeople: dinnerAdults + dinnerChildren7_12 + dinnerChildren0_6,
            nights: mealCosts.billableDinnerNights,
            totalCost: mealCosts.dinner
          } : null
        },
        ...(discountApplied && {
          discountCode: discountApplied.code,
          originalPrice: roomPrice || 0,
          discountAmount: discountApplied.discount_amount || 0
        }),
        invoice_type: invoiceType,
        invoice_name: invoiceName || '',
        ...(invoiceType === 'individual'
          ? { invoice_tckn: invoiceTckn || '', invoice_address: invoiceAddress || '', invoice_district: invoiceDistrict || '', invoice_city: invoiceCity || '', invoice_postal_code: invoicePostalCode || '' }
          : {
              invoice_tax_number: invoiceTaxNumber || '',
              invoice_tax_office: invoiceTaxOffice || '',
              invoice_address: invoiceAddress || '',
              invoice_district: invoiceDistrict || '',
              invoice_city: invoiceCity || '',
              invoice_postal_code: invoicePostalCode || ''
            }
        )
      };

      const response = await roomsAPI.book(bookingData);

      if (response.data.success) {
        const { iframeUrl, reservationId } = response.data.data;
        if (iframeUrl) {
          setPaymentUrl(iframeUrl);
          setCreatedBookingId(reservationId);
          setShowPaymentModal(true);
        } else {
          alert('Rezervasyon oluşturuldu: ' + reservationId);
          onClose();
        }
      }
    } catch (err: any) {
      alert('Rezervasyon oluşturulamadı: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalGuests = adultsCount + childrenCount;
  const standardCap = roomCapacity?.standardCapacity || roomCapacity?.maxOccupancy || 2;
  const maxOcc = roomCapacity?.maxOccupancy || 2;

  return (
    <>
      {/* Kapasite Aşımı Modal - Rezervasyon Yapılamaz */}
      {capacityExceeded && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '20px' }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0, color: '#dc3545', fontSize: '1.5rem', marginBottom: '1rem' }}>🚫 Kapasite Aşımı</h3>
            
            <div style={{ background: '#f8d7da', padding: '1.25rem', borderRadius: '8px', marginBottom: '1.5rem', lineHeight: '1.7', fontSize: '0.95rem', border: '1px solid #f5c6cb' }}>
              <p style={{ marginTop: 0, fontWeight: '600', color: '#721c24' }}>
                Seçtiğiniz <strong>{roomName}</strong> odası için <strong>{adultsCount} yetişkin + {childrenCount} çocuk</strong> (toplam {totalGuests} kişi) konaklaması talep ettiniz.
              </p>
              
              <p style={{ color: '#721c24', marginBottom: 0 }}>
                Bu odanın <strong>maksimum kapasitesi {maxOcc} kişi</strong>dir. Talep ettiğiniz <strong>{totalGuests} kişilik</strong> konaklama bu oda için mümkün değildir.
              </p>
            </div>
            
            <div style={{ background: '#e2e3e5', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#383d41', fontWeight: '500' }}>
                💡 Lütfen daha az kişi sayısı ile tekrar deneyin veya daha büyük kapasiteli bir oda seçin.
              </p>
            </div>
            
            <button 
              onClick={onClose}
              style={{ width: '100%', padding: '12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* Ek Yatak Onay Modal - stdCap < kişi <= maxOcc */}
      {showExtraBedWarning && !extraBedAcknowledged && !capacityExceeded && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '20px' }}>
          <div className="capacity-warning-modal" style={{ background: '#fff', padding: '30px', borderRadius: '12px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0, color: '#d4af37', fontSize: '1.5rem', marginBottom: '1rem' }}>⚠️ Önemli Bilgilendirme – Ek Yatak</h3>
            
            <div style={{ background: '#f8f9fa', padding: '1.25rem', borderRadius: '8px', marginBottom: '1.5rem', lineHeight: '1.7', fontSize: '0.95rem' }}>
              <p style={{ marginTop: 0, fontWeight: '600', color: '#333' }}>
                Seçtiğiniz <strong>{roomName}</strong> odası için <strong>{adultsCount} yetişkin + {childrenCount} çocuk</strong> (toplam {totalGuests} kişi) konaklaması talep ettiniz.
              </p>
              
              <p>Bu odanın <strong>standart kapasitesi {standardCap} kişi</strong> olup, talep ettiğiniz {totalGuests} kişilik konaklama için <strong>ek yatak açılması gerekmektedir.</strong></p>
              
              <p style={{ color: '#856404', fontWeight: '600' }}>Ek yatak açıldığında <strong>oda alanı sınırlı</strong> olacağından konfor standart kapasiteye göre daha düşük olabilir.</p>
              
              <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
              
              <p style={{ fontWeight: '600', color: '#d4af37', marginBottom: '0.5rem' }}>Rezervasyonunuza devam ederek;</p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: 0 }}>
                <li>Odanın standart kapasitesinin <strong>{standardCap} kişi</strong> olduğunu,</li>
                <li>Ek yatağın <strong>istisnai olarak açıldığını</strong>,</li>
                <li><strong>Oda içi konforun sınırlı olabileceğini</strong>,</li>
                <li>Bu durumun size önceden bildirildiğini</li>
              </ul>
              <p style={{ marginBottom: 0, marginTop: '0.5rem' }}>kabul etmiş olursunuz.</p>
            </div>
            
            <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #ffc107' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404', fontWeight: '500' }}>
                ⚠️ Ek yatak nedeniyle oluşabilecek konfor beklentileri sebebiyle yapılacak <strong>iptal veya ücret iadesi talepleri kabul edilmemektedir.</strong>
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button 
                onClick={() => {
                  setExtraBedAcknowledged(true);
                  setShowExtraBedWarning(false);
                }}
                style={{ flex: 1, padding: '12px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
              >
                ✓ Anladım, Devam Et
              </button>
              <button 
                onClick={() => {
                  setShowExtraBedWarning(false);
                  onClose();
                }}
                style={{ flex: 1, padding: '12px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' }}
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="modal-overlay" style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.7)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          background: 'white', padding: '2rem', borderRadius: '12px',
          maxWidth: '500px', width: '90%', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
        }}>
          <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
            Rezervasyon Tamamla
          </h3>

          <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>{roomName}</p>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666' }}>
              {checkInDate} — {checkOutDate} ({nights} Gece)
            </p>
            <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold', fontSize: '1.2rem', color: '#d4af37' }}>
              {roomPrice?.toLocaleString('tr-TR')} TL
            </p>
          </div>

          <form onSubmit={handleBooking} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="form-group">
              <label>Ad Soyad</label>
              <input
                type="text"
                name="guestName"
                className="form-input"
                required
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Örn: Ali Yılmaz"
              />
              {guestName.length > 0 && !isValidFullName(guestName) && (
                <small style={{ color: '#dc3545', marginTop: '0.3rem', display: 'block' }}>
                  Lütfen ad ve soyadınızı birlikte yazın (örn: Ali Yılmaz)
                </small>
              )}
            </div>
            <div className="form-group">
              <label>E-posta</label>
              <input type="email" name="guestEmail" className="form-input" required />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input type="tel" name="guestPhone" className="form-input" required placeholder="+90 555..." />
            </div>

            {/* Misafir Grubu Cinsiyeti */}
            <div className="form-group">
              <label style={{ fontWeight: '600' }}>
                Misafir Grubu Cinsiyeti <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                value={guestGroupType ?? ''}
                onChange={(e) => setGuestGroupType(e.target.value as '' | 'mixed' | 'male_only')}
                className="form-input"
                required
                style={{ cursor: 'pointer' }}
              >
                <option value="">— Lütfen seçiniz —</option>
                <option value="mixed">Aile / Karma grup (en az bir kadın misafir bulunmaktadır)</option>
                <option value="male_only">Yalnızca erkek misafir(ler)</option>
              </select>
              {guestGroupType === 'male_only' && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.85rem 1rem',
                  background: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  color: '#721c24',
                  lineHeight: '1.6'
                }}>
                  <strong>⛔ Rezervasyon yapılamaz.</strong><br />
                  Villa Polikne, butik bir aile işletmesi konseptiyle hizmet vermektedir. Tesisin genel huzuru ve aile ortamının korunması amacıyla yalnızca erkek misafirlerden oluşan grup veya tekil rezervasyonlar <strong>kabul edilmemektedir</strong>.
                </div>
              )}
            </div>

            {/* Meal Add-ons */}
            <div style={{
              background: '#fff8e1',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #ffd54f'
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#f57c00', fontSize: '1rem' }}>
                🍽️ Ek Hizmetler
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', padding: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <input
                    type="checkbox"
                    checked={includeBreakfast}
                    onChange={(e) => setIncludeBreakfast(e.target.checked)}
                    style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>🍳 Kahvaltı Ekle</div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                      Konaklama süresince her gün için kahvaltı rezervasyonu
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#f57c00', fontWeight: '600' }}>
                      Yetişkin: ₺{mealPricing.breakfastAdult} |
                      7-12 yaş: ₺{mealPricing.breakfastChild7_12} |
                      0-6 yaş: ₺0
                    </div>
                    {includeBreakfast && (
                      <>
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👤 Yetişkin:</label>
                            <select
                              value={breakfastAdults}
                              onChange={(e) => setBreakfastAdults(parseInt(e.target.value))}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                            >
                              {Array.from({length: adultsCount + childrenAges.filter(a => a >= 13).length + 1}, (_, i) => i).map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (7-12):</label>
                            <select
                              value={breakfastChildren7_12}
                              onChange={(e) => setBreakfastChildren7_12(parseInt(e.target.value))}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                            >
                              {Array.from({length: childrenAges.filter(a => a >= 7 && a <= 12).length + 1}, (_, i) => i).map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (0-6):</label>
                            <select
                              value={breakfastChildren0_6}
                              onChange={(e) => setBreakfastChildren0_6(parseInt(e.target.value))}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                            >
                              {Array.from({length: childrenAges.filter(a => a >= 0 && a <= 6).length + 1}, (_, i) => i).map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {(() => {
                          const mc = calculateMealCosts();
                          return (
                            <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#e8f5e9', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold', color: '#2e7d32' }}>
                              ☕ {mc.billableBreakfastNights} gece × {breakfastAdults + breakfastChildren7_12 + breakfastChildren0_6} kişi = ₺{mc.breakfast.toLocaleString('tr-TR')}
                              {mc.excludedBreakfasts > 0 && <div style={{ fontSize:'0.78rem', color:'#e65100', fontWeight:'normal', marginTop:'2px' }}>({mc.excludedBreakfasts} kapalı gün sabahı hariç)</div>}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', padding: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <input
                    type="checkbox"
                    checked={includeDinner}
                    onChange={(e) => setIncludeDinner(e.target.checked)}
                    style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>🍷 Akşam Yemeği Ekle</div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                      Konaklama süresince her gün için akşam yemeği rezervasyonu
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#f57c00', fontWeight: '600' }}>
                      Yetişkin: ₺{mealPricing.dinnerAdult} |
                      7-12 yaş: ₺{mealPricing.dinnerChild7_12} |
                      0-6 yaş: ₺{mealPricing.dinnerChild0_6}
                    </div>
                    {includeDinner && (
                      <>
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👤 Yetişkin:</label>
                            <select
                              value={dinnerAdults}
                              onChange={(e) => setDinnerAdults(parseInt(e.target.value))}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                            >
                              {Array.from({length: adultsCount + childrenAges.filter(a => a >= 13).length + 1}, (_, i) => i).map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (7-12):</label>
                            <select
                              value={dinnerChildren7_12}
                              onChange={(e) => setDinnerChildren7_12(parseInt(e.target.value))}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                            >
                              {Array.from({length: childrenAges.filter(a => a >= 7 && a <= 12).length + 1}, (_, i) => i).map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (0-6):</label>
                            <select
                              value={dinnerChildren0_6}
                              onChange={(e) => setDinnerChildren0_6(parseInt(e.target.value))}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                            >
                              {Array.from({length: childrenAges.filter(a => a >= 0 && a <= 6).length + 1}, (_, i) => i).map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {(() => {
                          const mc = calculateMealCosts();
                          return (
                            <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#e8f5e9', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold', color: '#2e7d32' }}>
                              🍽️ {mc.billableDinnerNights} gece × {dinnerAdults + dinnerChildren7_12 + dinnerChildren0_6} kişi = ₺{mc.dinner.toLocaleString('tr-TR')}
                              {mc.excludedDinners > 0 && <div style={{ fontSize:'0.78rem', color:'#e65100', fontWeight:'normal', marginTop:'2px' }}>({mc.excludedDinners} kapalı gün gecesi hariç)</div>}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </label>
              </div>

              {/* Pazartesi / Pazar uyarı onay kutuları */}
              {(() => {
                const excl = getMondayExclusions(checkInDate, checkOutDate);
                const showSunday = excl.breakfasts > 0;
                const showMonday = excl.dinners > 0;
                if (!showSunday && !showMonday) return null;
                return (
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {showSunday && (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem', background: '#fff3e0', borderRadius: '6px', border: '1px solid #ff9800', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={sundayMondayAgreed}
                          onChange={e => setSundayMondayAgreed(e.target.checked)}
                          style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.82rem', color: '#e65100', lineHeight: '1.5' }}>
                          Bazı günlerde tesisimizde kahvaltı ve akşam yemeği hizmeti verilmediğini, bu günlere denk gelen konaklama gecelerinde ücretlendirme dışı bırakıldığını okudum, anladım ve kabul ediyorum.
                        </span>
                      </label>
                    )}
                    {showMonday && (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem', background: '#fce4ec', borderRadius: '6px', border: '1px solid #e91e63', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={mondayAgreed}
                          onChange={e => setMondayAgreed(e.target.checked)}
                          style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.82rem', color: '#880e4f', lineHeight: '1.5' }}>
                          <strong>Kapalı günlerde tesisimizde akşam yemeği ve bazı hizmetlerin sunulmadığını</strong>, bu nedenle kapalı günlere denk gelen konaklamaların buna göre fiyatlandırıldığını okudum ve kabul ediyorum.
                        </span>
                      </label>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="form-group">
              <label>İndirim Kodu (Opsiyonel)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  className="form-input"
                  placeholder="İndirim kodunuz varsa girin"
                  disabled={!!discountApplied}
                  style={{ flex: 1 }}
                />
                {!discountApplied ? (
                  <button
                    type="button"
                    onClick={handleApplyDiscount}
                    disabled={discountLoading || !discountCode.trim()}
                    style={{
                      padding: '0.5rem 1rem',
                      background: discountLoading ? '#ccc' : '#d4af37',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: discountLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {discountLoading ? 'Kontrol...' : 'Uygula'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleRemoveDiscount}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Kaldır
                  </button>
                )}
              </div>
              {discountMessage.text && (
                <small style={{
                  display: 'block',
                  marginTop: '0.5rem',
                  color: discountMessage.type === 'success' ? '#27ae60' :
                         discountMessage.type === 'error' ? '#e74c3c' : '#f39c12'
                }}>
                  {discountMessage.text}
                </small>
              )}
            </div>

            {/* Fiyat Özeti */}
            <div style={{
              background: '#f8f9fa',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <span>Konaklama ({nights} gece):</span>
                <span style={{ fontWeight: 'bold' }}>
                  {roomPrice?.toLocaleString('tr-TR')} TL
                </span>
              </div>
              {discountApplied && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  flexWrap: 'wrap',
                  color: '#27ae60'
                }}>
                  <span>İndirim ({discountApplied.code}):</span>
                  <span style={{ fontWeight: 'bold' }}>
                    -{discountApplied.discount_amount.toFixed(2)} TL
                  </span>
                </div>
              )}
              {includeBreakfast && calculateMealCosts().breakfast > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  flexWrap: 'wrap',
                  color: '#f57c00'
                }}>
                  <span>🍳 Kahvaltı ({calculateMealCosts().billableBreakfastNights} gün{calculateMealCosts().excludedBreakfasts > 0 ? ', kapalı günler hariç' : ''}):</span>
                  <span style={{ fontWeight: 'bold' }}>
                    +{calculateMealCosts().breakfast.toLocaleString('tr-TR')} TL
                  </span>
                </div>
              )}
              {includeDinner && calculateMealCosts().dinner > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  flexWrap: 'wrap',
                  color: '#f57c00'
                }}>
                  <span>🍷 Akşam Yemeği ({calculateMealCosts().billableDinnerNights} gün{calculateMealCosts().excludedDinners > 0 ? ', kapalı günler hariç' : ''}):</span>
                  <span style={{ fontWeight: 'bold' }}>
                    +{calculateMealCosts().dinner.toLocaleString('tr-TR')} TL
                  </span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: '2px solid #dee2e6',
                fontSize: '1.2rem'
              }}>
                <span style={{ fontWeight: 'bold' }}>Toplam:</span>
                <span style={{ fontWeight: 'bold', color: '#d4af37' }}>
                  {(() => {
                    const finalPrice = discountApplied ? discountApplied.final_amount : (roomPrice || 0);
                    const totalWithMeals = finalPrice + calculateMealCosts().total;
                    return totalWithMeals.toLocaleString('tr-TR');
                  })()} TL
                </span>
              </div>
            </div>

            <div className="form-group">
              <label>Notlar</label>
              <textarea name="specialRequests" className="form-input" rows={3}></textarea>
            </div>

            {/* Fatura Bilgileri */}
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.75rem', fontSize: '0.95rem', color: '#2c3e50' }}>🧾 Fatura Bilgileri (Zorunlu)</div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                {(['individual', 'corporate'] as const).map((type) => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input
                      type="radio"
                      name="invoice_type_radio"
                      value={type}
                      checked={invoiceType === type}
                      onChange={() => setInvoiceType(type)}
                    />
                    {type === 'individual' ? 'Bireysel' : 'Kurumsal'}
                  </label>
                ))}
              </div>
              {invoiceType === 'individual' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <div>
                    <input type="text" className="form-input" placeholder="Ad Soyad (örn: Ali Yılmaz)" value={invoiceName} onChange={(e) => setInvoiceName(e.target.value)} required />
                    {invoiceName.length > 0 && !isValidFullName(invoiceName) && (
                      <small style={{ color: '#dc3545', marginTop: '0.3rem', display: 'block' }}>
                        Lütfen ad ve soyadınızı birlikte yazın (örn: Ali Yılmaz)
                      </small>
                    )}
                  </div>
                  <input type="text" className="form-input" placeholder="T.C. Kimlik No (isteğe bağlı)" value={invoiceTckn} onChange={(e) => setInvoiceTckn(e.target.value.replace(/\D/g, '').slice(0, 11))} maxLength={11} />
                  <input type="text" className="form-input" placeholder="Adres (Mahalle, Cadde, No)" value={invoiceAddress} onChange={(e) => setInvoiceAddress(e.target.value)} required />
                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <input type="text" className="form-input" placeholder="İlçe" value={invoiceDistrict} onChange={(e) => setInvoiceDistrict(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                    <input type="text" className="form-input" placeholder="İl" value={invoiceCity} onChange={(e) => setInvoiceCity(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                    <input type="text" className="form-input" placeholder="Posta Kodu" value={invoicePostalCode} onChange={(e) => setInvoicePostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} style={{ flex: '1 1 80px', minWidth: '80px' }} />
                  </div>
                </div>
              )}
              {invoiceType === 'corporate' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <input type="text" className="form-input" placeholder="Firma Ünvanı" value={invoiceName} onChange={(e) => setInvoiceName(e.target.value)} required />
                  <input type="text" className="form-input" placeholder="Vergi Numarası (VKN)" value={invoiceTaxNumber} onChange={(e) => setInvoiceTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} required />
                  <input type="text" className="form-input" placeholder="Vergi Dairesi" value={invoiceTaxOffice} onChange={(e) => setInvoiceTaxOffice(e.target.value)} required />
                  <input type="text" className="form-input" placeholder="Adres (Mahalle, Cadde, No)" value={invoiceAddress} onChange={(e) => setInvoiceAddress(e.target.value)} required />
                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <input type="text" className="form-input" placeholder="İlçe" value={invoiceDistrict} onChange={(e) => setInvoiceDistrict(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                    <input type="text" className="form-input" placeholder="İl" value={invoiceCity} onChange={(e) => setInvoiceCity(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                    <input type="text" className="form-input" placeholder="Posta Kodu" value={invoicePostalCode} onChange={(e) => setInvoicePostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} style={{ flex: '1 1 80px', minWidth: '80px' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Satış Sözleşmesi */}
            <div style={{ marginTop: '1rem' }}>
              <SalesAgreement
                isChecked={agreementAccepted}
                onCheckChange={setAgreementAccepted}
              />
            </div>

            {/* Fiyat Yükleme Hatası */}
            {mealPriceError && (
              <div style={{
                padding: '1rem',
                background: '#fff3cd',
                border: '2px solid #ff9800',
                borderRadius: '8px',
                color: '#856404',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                ⚠️ Fiyat bilgileri yüklenemedi. Rezervasyon şu anda yapılamıyor.
                Lütfen sayfayı yenileyip tekrar deneyin veya iletişime geçin.
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                type="submit"
                className="btn-book"
                style={{ flex: 2 }}
                disabled={(() => {
                  if (loading) return true;
                  if (mealPriceError) return true;
                  if (guestGroupType === 'male_only') return true;
                  if (!isValidFullName(guestName)) return true;
                  if (invoiceType === 'individual' && !isValidFullName(invoiceName)) return true;
                  if (!agreementAccepted) return true;
                  const excl = getMondayExclusions(checkInDate, checkOutDate);
                  if (excl.breakfasts > 0 && !sundayMondayAgreed) return true;
                  if (excl.dinners > 0 && !mondayAgreed) return true;
                  return false;
                })()}
              >
                {loading ? 'İşleniyor...' : 'ONAYLA VE ÖDE'}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{ flex: 1, border: 'none', background: 'transparent', color: '#888', cursor: 'pointer' }}
              >
                Vazgeç
              </button>
            </div>
          </form>
        </div>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); onClose(); }}
        iframeUrl={paymentUrl}
        bookingId={createdBookingId}
        skipAgreement={true}
      />
    </>
  );
};

export default BookingFormModal;
